import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { MultiSelectInput, useFormContext } from 'framework/form';
import useVacancyPositionSlotsService, {
  buildVacancyPositionSlotScope,
  isVacancyPositionSlotMatchingScope,
} from './useVacancyPositionSlotsService';
import { VacancyPositionSlotListItem } from './types';
import {
  PositionSlotOption,
  WorkingConditionFormValue,
  buildDefaultWorkingConditions,
  buildPositionSlotFormData,
  buildPositionSlotWorkingConditionsData,
  formatPositionSlotOptionLabel,
  includesSearchValue,
  mapPositionSlotResultsToOptions,
  mapPositionSlotsToOptions,
} from './vacancyPositionSlotFormData';

const VacancyPositionSlotsInput = () => {
  const { data = {}, onChangeCallback = () => {} } = useFormContext();
  const { getAvailablePositionSlots } = useVacancyPositionSlotsService();
  const [availablePositionSlots, setAvailablePositionSlots] = useState<VacancyPositionSlotListItem[]>([]);
  const [isLoadingPositionSlots, setIsLoadingPositionSlots] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<PositionSlotOption[]>([]);
  const searchScope = useMemo(
    () => buildVacancyPositionSlotScope({}, selectedOptions[0]?.positionSlot),
    [selectedOptions],
  );
  const searchScopeKey = JSON.stringify(searchScope);

  const matchingPositionSlots = useMemo(
    () => availablePositionSlots.filter(positionSlot => isVacancyPositionSlotMatchingScope(positionSlot, searchScope)),
    [availablePositionSlots, searchScope],
  );

  useEffect(() => {
    let isMounted = true;

    setIsLoadingPositionSlots(true);
    getAvailablePositionSlots()
      .then(positionSlots => {
        if (isMounted) {
          setAvailablePositionSlots(positionSlots);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingPositionSlots(false);
        }
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = ({ positionSlotIds = [] }: { positionSlotIds?: PositionSlotOption[] }) => {
    const firstSelectedPositionSlot = positionSlotIds[0]?.positionSlot;
    const workingConditions = Array.isArray(data.workingConditions)
      ? (data.workingConditions as WorkingConditionFormValue[])
      : undefined;

    if (!firstSelectedPositionSlot) {
      setSelectedOptions([]);
      onChangeCallback({
        positionSlotIds: [],
        ...buildPositionSlotFormData(undefined),
        workingConditions: buildDefaultWorkingConditions(),
      });

      return;
    }

    const selectedPositionSlotScope = buildVacancyPositionSlotScope({}, firstSelectedPositionSlot);
    const compatibleOptions = positionSlotIds.filter(option =>
      isVacancyPositionSlotMatchingScope(option.positionSlot, selectedPositionSlotScope),
    );

    setSelectedOptions(compatibleOptions);
    onChangeCallback({
      positionSlotIds: compatibleOptions.map(option => option.value),
      ...buildPositionSlotFormData(firstSelectedPositionSlot),
      ...buildPositionSlotWorkingConditionsData(workingConditions, firstSelectedPositionSlot),
    });
  };

  const loadSuggestions = (searchValue: object) =>
    Promise.resolve(
      matchingPositionSlots.filter(positionSlot => includesSearchValue(positionSlot, String(searchValue || ''))),
    );

  return (
    <Box>
      <MultiSelectInput
        key={`${searchScopeKey}-${availablePositionSlots.length}`}
        source="positionSlotIds"
        label="Position slots"
        placeholder="Select position slots"
        helpText="Selecting a position slot narrows the list to other eligible, unlinked slots that match it."
        defaultOptions={mapPositionSlotsToOptions(matchingPositionSlots)}
        debounceDelay={300}
        value={selectedOptions}
        onChangeCallback={handleChange}
        loadSuggestionsMethod={loadSuggestions}
        mapResults={mapPositionSlotResultsToOptions}
        formatOptionLabel={formatPositionSlotOptionLabel}
        noOptionsMessage={() =>
          isLoadingPositionSlots ? 'Loading position slots...' : 'No compatible position slots found.'
        }
      />

      <Typography variant="body1" color="textSecondary">
        Available position slots: {matchingPositionSlots.length}
      </Typography>
    </Box>
  );
};

export default VacancyPositionSlotsInput;
